import {
  getPreWriteSignedWriteUrl,
  createMultipartUpload,
  signMultipartUploadParts,
  completeMultipartUpload,
  abortMultipartUpload,
} from './s3Services';

/**
 * Files at or below this size go through a single PUT — one round trip is
 * cheaper than the create/sign/complete handshake, and the upload is short
 * enough that resumability buys nothing.
 */
const SINGLE_PUT_MAX_BYTES = 16 * 1024 * 1024;

/**
 * Parts uploaded concurrently for one file. A single TCP stream is capped by the
 * bandwidth-delay product well below the available link speed, so pushing several
 * parts at once is what actually makes a multi-GB upload fast.
 */
const PART_CONCURRENCY = 5;

/** Part URLs are signed in batches this size, on demand, rather than all upfront. */
const SIGN_BATCH_SIZE = 10;

/** Attempts per part before the whole upload is failed. A retry costs one part, not the file. */
const MAX_PART_ATTEMPTS = 3;

const RETRY_BASE_DELAY_MS = 500;

/**
 * Metadata that makes an upload resumable. When supplied on a fresh upload, the backend records a
 * pending-upload row (keyed by taskId) at create time, so the upload can be resumed after a
 * logout, a crash or a tab close.
 *
 * Only intrinsic file facts belong here — user-entered metadata is not persisted.
 */
export interface PendingUploadMeta {
  module: string;
  fileName: string;
  fileType?: string | undefined;
  assetCategory?: string | undefined;
  devOrderId?: string | null | undefined;
  scopeKey?: string | null | undefined;
  addToDesign?: boolean | undefined;
  isVisibleToSupplier?: boolean | undefined;
  documentType?: string | null | undefined;
  expiredOn?: string | null | undefined;
  libId?: string | null | undefined;
  bomId?: string | null | undefined;
  bomType?: string | null | undefined;
  docType?: string | null | undefined;
  colourWay?: string | null | undefined;
  placement?: string | null | undefined;
  reference?: string | null | undefined;
  fileTitle?: string | null | undefined;
  productNumber?: string | null | undefined;
}

/**
 * State needed to resume a previously-started multipart upload: the existing uploadId, the
 * original part plan, and the parts S3 already stored (which are skipped). Present only on a
 * resume; a fresh upload omits it.
 */
export interface ResumeState {
  uploadId: string;
  partSize: number;
  partCount: number;
  uploadedPartNumbers: number[];
}

export interface UploadToS3Options {
  /** Called with 0-100 as bytes land. */
  onProgress?: ((percent: number) => void) | undefined;
  /** Aborts the upload; in-flight parts are cancelled and the S3 upload is aborted. */
  signal?: AbortSignal | undefined;
  /** Bucket alias understood by the backend ('Dpp', 'IMPORT-EXCEL', …). Defaults to the main bucket. */
  section?: string | undefined;
  /** Client task id shared with the floating widget row and the pending-upload record. */
  taskId?: string | undefined;
  /** When set, the backend registers a resumable pending-upload record at create time. */
  pendingMeta?: PendingUploadMeta | undefined;
  /** When set, resumes an existing multipart upload instead of creating a new one. */
  resume?: ResumeState | undefined;
  /**
   * Consulted only when the upload is aborted. When it returns true (a logout "detach"), the S3
   * multipart upload is left intact so it can be resumed later, instead of being aborted and its
   * parts discarded.
   */
  keepUploadOnAbort?: (() => boolean) | undefined;
}

interface PutBlobOptions {
  contentType?: string | undefined;
  onBytes?: ((loaded: number) => void) | undefined;
  signal?: AbortSignal | undefined;
}

class AbortedError extends Error {
  constructor() {
    super('Upload aborted');
    this.name = 'AbortedError';
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Extracts the Code/Message S3 returns in its XML error body so a failed part
 * reports why it failed rather than a bare status number.
 */
function describeUploadFailure(status: number, responseText: string): string {
  const code = /<Code>([^<]+)<\/Code>/.exec(responseText)?.[1];
  const message = /<Message>([^<]+)<\/Message>/.exec(responseText)?.[1];
  const detail = [code, message].filter(Boolean).join(': ');
  return detail ? `Upload failed with status ${status} (${detail})` : `Upload failed with status ${status}`;
}

/**
 * PUTs a blob to a presigned URL, reporting bytes-sent as they go, and resolves
 * with the response's ETag header when the browser can see it.
 *
 * A missing ETag is not fatal: S3 hides the header unless the bucket CORS policy
 * names it under ExposeHeaders, and the server rebuilds the authoritative part
 * list with ListParts at completion time.
 */
function putBlob(url: string, blob: Blob, options: PutBlobOptions): Promise<string | null> {
  const { contentType, onBytes, signal } = options;

  return new Promise<string | null>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new AbortedError());
      return;
    }

    const xhr = new XMLHttpRequest();
    const onAbortSignal = () => xhr.abort();

    xhr.open('PUT', url, true);
    if (contentType) {
      xhr.setRequestHeader('Content-Type', contentType);
    }

    if (onBytes) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onBytes(event.loaded);
      };
    }

    const cleanup = () => signal?.removeEventListener('abort', onAbortSignal);

    xhr.onload = () => {
      cleanup();
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(describeUploadFailure(xhr.status, xhr.responseText || '')));
        return;
      }
      resolve(xhr.getResponseHeader('ETag'));
    };
    xhr.onerror = () => {
      cleanup();
      reject(new Error('Upload failed'));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new AbortedError());
    };

    signal?.addEventListener('abort', onAbortSignal);
    xhr.send(blob);
  });
}

/**
 * Uploads a small file in a single presigned PUT.
 */
async function uploadSinglePut(file: File, objectKey: string, options: UploadToS3Options): Promise<string> {
  const writeRes = await getPreWriteSignedWriteUrl({ objectKey, expiresInSeconds: 3600 });
  const writeUrl = writeRes?.data;
  if (!writeUrl) throw new Error('Failed to get upload URL');

  await putBlob(writeUrl, file, {
    contentType: file.type || 'application/octet-stream',
    signal: options.signal,
    onBytes: (loaded) => {
      options.onProgress?.(Math.round((loaded / file.size) * 100));
    },
  });

  options.onProgress?.(100);
  return objectKey;
}

interface PartUrlSigner {
  get: (partNumber: number) => Promise<string>;
  invalidate: (partNumber: number) => void;
}

/**
 * Signs part URLs lazily, one batch at a time, memoising each batch so concurrent
 * workers share a single request. Because a URL is issued moments before it is
 * used, an upload running for hours never meets an expired signature.
 */
function createPartUrlSigner(params: { objectKey: string; uploadId: string; partCount: number; section?: string | undefined }): PartUrlSigner {
  const { objectKey, uploadId, partCount, section } = params;
  const signedUrls = new Map<number, string>();
  const batches = new Map<number, Promise<void>>();

  const batchIndexOf = (partNumber: number) => Math.floor((partNumber - 1) / SIGN_BATCH_SIZE);

  const signBatch = (batchIndex: number): Promise<void> => {
    const firstPart = batchIndex * SIGN_BATCH_SIZE + 1;
    const lastPart = Math.min(firstPart + SIGN_BATCH_SIZE - 1, partCount);
    const partNumbers: number[] = [];
    for (let candidate = firstPart; candidate <= lastPart; candidate += 1) {
      partNumbers.push(candidate);
    }

    return signMultipartUploadParts({ objectKey, uploadId, partNumbers, section })
      .then((parts) => {
        (parts ?? []).forEach((part) => signedUrls.set(part.partNumber, part.url));
      })
      .catch((error) => {
        batches.delete(batchIndex);
        throw error;
      });
  };

  return {
    get: async (partNumber: number): Promise<string> => {
      const batchIndex = batchIndexOf(partNumber);
      if (!batches.has(batchIndex)) {
        batches.set(batchIndex, signBatch(batchIndex));
      }
      await batches.get(batchIndex);

      const url = signedUrls.get(partNumber);
      if (!url) throw new Error(`Failed to sign part ${partNumber}`);
      return url;
    },
    invalidate: (partNumber: number): void => {
      signedUrls.delete(partNumber);
      batches.delete(batchIndexOf(partNumber));
    },
  };
}

/**
 * Uploads one part, retrying with backoff on failure. Each retry re-signs the URL
 * so a stale signature is self-healing, and a transient drop costs one part rather
 * than the whole transfer. Returns the part's ETag, or null when the browser
 * cannot read it — the server resolves those from S3 at completion time.
 */
async function uploadPartWithRetry(params: {
  chunk: Blob;
  partNumber: number;
  signer: PartUrlSigner;
  signal?: AbortSignal | undefined;
  onBytes: (loaded: number) => void;
}): Promise<string | null> {
  const { chunk, partNumber, signer, signal, onBytes } = params;
  let lastError: unknown = new Error(`Failed to upload part ${partNumber}`);

  for (let attempt = 1; attempt <= MAX_PART_ATTEMPTS; attempt += 1) {
    if (signal?.aborted) throw new AbortedError();
    try {
      const url = await signer.get(partNumber);
      return await putBlob(url, chunk, { signal, onBytes });
    } catch (error) {
      if (error instanceof AbortedError) throw error;
      lastError = error;
      onBytes(0);
      signer.invalidate(partNumber);
      if (attempt < MAX_PART_ATTEMPTS) {
        await delay(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
      }
    }
  }

  throw lastError;
}

interface MultipartPlan {
  uploadId: string;
  partSize: number;
  partCount: number;
}

/**
 * Resolves the multipart plan for an upload: on resume it reuses the plan the original upload
 * used, otherwise it opens a new multipart upload — registering a resumable pending record when
 * pendingMeta is supplied.
 *
 * Reusing the stored plan is what makes resume correct: part boundaries must line up byte-for-byte
 * with the parts S3 already holds, so the plan can never be recomputed with different inputs.
 */
async function resolveMultipartPlan(file: File, objectKey: string, options: UploadToS3Options): Promise<MultipartPlan> {
  const { section, taskId, pendingMeta, resume } = options;
  if (resume) {
    return { uploadId: resume.uploadId, partSize: resume.partSize, partCount: resume.partCount };
  }

  const plan = await createMultipartUpload({
    objectKey,
    contentType: file.type || 'application/octet-stream',
    fileSize: file.size,
    section,
    ...(taskId ? { taskId } : {}),
    ...(pendingMeta ? { pendingMeta } : {}),
  });

  if (!plan?.uploadId) throw new Error('Failed to start multipart upload');
  return { uploadId: plan.uploadId, partSize: plan.partSize, partCount: plan.partCount };
}

/**
 * Builds the per-part byte-progress array, pre-seeding the parts S3 already holds to their full
 * size so a resumed upload's progress bar starts partway through rather than back at 0%.
 */
function seedLoadedByPart(params: {
  partCount: number;
  partSize: number;
  fileSize: number;
  alreadyUploaded: Set<number>;
}): number[] {
  const { partCount, partSize, fileSize, alreadyUploaded } = params;
  const loadedByPart = new Array<number>(partCount + 1).fill(0);
  alreadyUploaded.forEach((partNumber) => {
    if (partNumber >= 1 && partNumber <= partCount) {
      const start = (partNumber - 1) * partSize;
      loadedByPart[partNumber] = Math.min(start + partSize, fileSize) - start;
    }
  });
  return loadedByPart;
}

/**
 * Drains a queue of part numbers through a fixed pool of concurrent workers, each taking the next
 * part as soon as it frees up. A shared cursor rather than fixed slices keeps every worker busy
 * when parts finish at different speeds.
 */
async function runPartWorkers(partNumbers: number[], uploadPart: (partNumber: number) => Promise<void>): Promise<void> {
  let queueIndex = 0;
  const workerCount = Math.min(PART_CONCURRENCY, Math.max(1, partNumbers.length));
  const workers = Array.from({ length: workerCount }, async () => {
    for (;;) {
      const partNumber = partNumbers[queueIndex];
      if (partNumber === undefined) return;
      queueIndex += 1;
      await uploadPart(partNumber);
    }
  });
  await Promise.all(workers);
}

/**
 * Uploads a large file as parallel multipart parts.
 *
 * Running several parts at once is what makes a multi-GB upload fast — one TCP
 * stream cannot saturate the link on its own. Any unrecoverable failure or caller
 * abort tears the upload down via AbortMultipartUpload so no orphaned parts are
 * left accruing storage cost — unless the abort was a detach (logout), in which case the parts
 * are deliberately kept so the upload can be resumed.
 */
async function uploadMultipart(file: File, objectKey: string, options: UploadToS3Options): Promise<string> {
  const { section, signal, onProgress, resume } = options;

  const { uploadId, partSize, partCount } = await resolveMultipartPlan(file, objectKey, options);

  /** Parts S3 already holds from a prior run — skipped on resume, empty on a fresh upload. */
  const alreadyUploaded = new Set<number>(resume?.uploadedPartNumbers ?? []);

  const signer = createPartUrlSigner({ objectKey, uploadId, partCount, section });
  const etags = new Map<number, string>();
  const loadedByPart = seedLoadedByPart({ partCount, partSize, fileSize: file.size, alreadyUploaded });

  /** Held below 100 until completion so the bar cannot claim done while parts are still in flight. */
  const reportProgress = () => {
    if (!onProgress) return;
    const loaded = loadedByPart.reduce((sum, bytes) => sum + bytes, 0);
    onProgress(Math.min(99, Math.round((loaded / file.size) * 100)));
  };

  const uploadPart = async (partNumber: number): Promise<void> => {
    const start = (partNumber - 1) * partSize;
    const chunk = file.slice(start, Math.min(start + partSize, file.size));

    const etag = await uploadPartWithRetry({
      chunk,
      partNumber,
      signer,
      signal,
      onBytes: (loaded) => {
        loadedByPart[partNumber] = loaded;
        reportProgress();
      },
    });

    if (etag) {
      etags.set(partNumber, etag);
    }
    loadedByPart[partNumber] = chunk.size;
    reportProgress();
  };

  reportProgress();

  /** Only the parts S3 does not already hold — on a fresh upload that is every part. */
  const partsToUpload: number[] = [];
  for (let partNumber = 1; partNumber <= partCount; partNumber += 1) {
    if (!alreadyUploaded.has(partNumber)) partsToUpload.push(partNumber);
  }

  try {
    await runPartWorkers(partsToUpload, uploadPart);

    const parts = Array.from(etags.entries())
      .map(([partNumber, etag]) => ({ PartNumber: partNumber, ETag: etag }))
      .sort((left, right) => left.PartNumber - right.PartNumber);

    await completeMultipartUpload({ objectKey, uploadId, parts, partCount, section });
    onProgress?.(100);
    return objectKey;
  } catch (error) {
    const keepForResume = signal?.aborted === true && options.keepUploadOnAbort?.() === true;
    if (!keepForResume) {
      await abortMultipartUpload({ objectKey, uploadId, section });
    }
    throw error;
  }
}

/**
 * Uploads a file to S3, picking a single PUT for small files and a parallel
 * multipart upload for anything larger. Returns the object key on success.
 */
export async function uploadFileToS3(file: File, objectKey: string, options: UploadToS3Options = {}): Promise<string> {
  if (file.size <= SINGLE_PUT_MAX_BYTES) {
    return uploadSinglePut(file, objectKey, options);
  }
  return uploadMultipart(file, objectKey, options);
}

export { SINGLE_PUT_MAX_BYTES, PART_CONCURRENCY };
